import type { PortalPaymentDetail } from '@erp/shared';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Card, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatMoney } from '../../lib/money.js';

export function PortalPaymentDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const { id = '' } = useParams();
  const [pay, setPay] = useState<PortalPaymentDetail | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPay(null);
    setNotFound(false);
    void customerApi()
      .get<PortalPaymentDetail>(`/payments/${id}`)
      .then((r) => setPay(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const money = (v: string, c: string): string => formatMoney(v, { locale: i18n.language, currency: c });

  return (
    <div className="flex flex-col gap-4 pb-16 lg:pb-0">
      <Link to="/portal/statement?tab=payments" className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t('payments.detail.back')}
      </Link>

      {notFound ? (
        <Card className="p-6 text-sm text-danger">{t('loadError')}</Card>
      ) : !pay ? (
        <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
      ) : (
        <>
          <h1 className="text-2xl font-semibold">{t('payments.detail.title', { number: pay.docNumber })}</h1>
          <div className="grid grid-cols-2 gap-3 text-sm sm:max-w-md">
            <div className="text-fg-muted">{t('columns.date')}</div>
            <div>{pay.paymentDate}</div>
            <div className="text-fg-muted">{t('columns.amount')}</div>
            <div className="font-mono tabular-nums">{money(pay.amount, pay.currencyCode)}</div>
            <div className="text-fg-muted">{t('columns.method')}</div>
            <div>{pay.method ?? '—'}</div>
            <div className="text-fg-muted">{t('payments.detail.reference')}</div>
            <div>{pay.reference ?? '—'}</div>
          </div>

          {pay.allocations.length > 0 && (
            <Card className="overflow-hidden p-0">
              <div className="border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t('payments.detail.allocations')}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {pay.allocations.map((a) => (
                    <tr key={a.targetId} className="border-t border-border first:border-t-0">
                      <td className="px-4 py-2">
                        {a.targetNumber ? (
                          <Link to={`/portal/invoices/${a.targetId}`} className="font-mono text-xs text-brand hover:underline">
                            {a.targetNumber}
                          </Link>
                        ) : (
                          <span className="text-fg-muted">{a.targetType}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-end font-mono tabular-nums">{money(a.amount, pay.currencyCode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
